import React, { useState, useRef, useEffect } from 'react';
import { ScanLine, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import apiClient from '@/services/apiClient';

export const PieceScanStep = ({ sku, scannedCodes, onPiece }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null); // { ok, text }
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 80); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const usn = value.trim();
    if (!usn || busy) return;
    setBusy(true);
    try {
      const res = await apiClient.post('/wms/sfg/floor-clearance/scan-usn', {
        usn_code: usn, sku_code: sku?.sku_code || null, scanned: scannedCodes,
      });
      onPiece(res.data.piece);
      setLast({ ok: true, text: `${res.data.piece.usn_code} added (from ${res.data.piece.old_bundle_code})` });
    } catch (err) {
      setLast({ ok: false, text: err.response?.data?.detail || `Could not add ${usn}` });
    } finally {
      setValue('');
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  };

  return (
    <section className="bg-white rounded-xl border border-[#E5DFD0] p-4" data-testid="fc-piece-step">
      <div className="flex items-center gap-2 mb-3">
        <span className="h-6 w-6 rounded-full bg-[#1A1F2E] text-white text-xs font-bold flex items-center justify-center">2</span>
        <ScanLine className="h-4 w-4 text-[#2D5F4F]" />
        <h2 className="text-sm font-semibold text-[#1A1F2E]">Scan each piece (USN) going into this bundle</h2>
      </div>
      {sku && (
        <div className="mb-3 rounded-lg bg-[#F3EFE6] px-3 py-2 text-sm" data-testid="fc-sku-lock">
          <span className="text-[#5A5F6E]">Item locked to </span>
          <span className="font-mono font-semibold">{sku.sku_code}</span>
          <span className="text-[#5A5F6E]"> · {sku.design_name || '-'} {sku.shade ? `· ${sku.shade}` : ''} {sku.size ? `· ${sku.size}` : ''}</span>
        </div>
      )}
      <form onSubmit={submit} className="relative">
        <Input ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)} disabled={busy}
          placeholder="Scan or type USN..." className="font-mono h-12 text-base pr-10" data-testid="fc-usn-input" />
        {busy && <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-4 text-gray-400" />}
      </form>
      {last && (
        <div className={`mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${last.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}
          data-testid={last.ok ? 'fc-scan-success' : 'fc-scan-error'}>
          {last.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 mt-0.5 shrink-0" />}
          <span>{last.text}</span>
        </div>
      )}
    </section>
  );
};
